import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api, orderService, invoiceService, deliveryNoteService } from '../services/api';
import { formatCurrency, formatDate, formatTime, formatOrderStatus, getStatusColor } from '../utils/formatters';
import { FaSync, FaCloudDownloadAlt, FaEllipsisV, FaEdit, FaTrash, FaStickyNote, FaTruck, FaCheckSquare, FaSquare, FaTimes, FaEye, FaUndo, FaTrashAlt, FaChevronDown, FaChevronRight, FaPrint, FaUpload, FaClipboardList, FaImages, FaArchive } from 'react-icons/fa';
import EditLineModal from '../components/Orders/EditLineModal';
import OutOfStockModal from '../components/Orders/OutOfStockModal';
import LineNotesModal from '../components/Orders/LineNotesModal';
import CreateDeliveryNoteModal from '../components/Orders/CreateDeliveryNoteModal';
import CalculatorEditModal from '../components/Calculator/CalculatorEditModal';

const OrdersContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 0.5rem;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 0 1rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FilterTab = styled.button`
  padding: 0.5rem 0.75rem;
  background: ${props => props.$active ? '#2196F3' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#666'};
  border: none;
  border-bottom: 3px solid ${props => props.$active ? '#2196F3' : 'transparent'};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: ${props => props.$active ? '600' : '400'};
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$active ? '#2196F3' : '#f5f5f5'};
    color: ${props => props.$active ? 'white' : '#333'};
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const SearchBar = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    width: 300px;
  }
`;

const StatusFiltersContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  @media (min-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
`;

const StatusFilterButton = styled.button`
  padding: 0.35rem 0.5rem;
  border: 1px solid ${props => props.$active ? props.$color : '#ddd'};
  background: ${props => props.$active ? props.$color : 'white'};
  color: ${props => props.$active ? 'white' : props.$color || '#666'};
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 4px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  &:active {
    transform: translateY(0);
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    gap: 0.5rem;
  }
`;

const ClearFilterButton = styled.button`
  padding: 0.35rem 0.5rem;
  border: 1px solid #666;
  background: #666;
  color: white;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.2s;
  margin-left: 0.25rem;
  border-radius: 4px;

  &:hover {
    background: #555;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    margin-left: 0.5rem;
  }
`;

const SyncButton = styled.button`
  padding: 0.4rem 0.6rem;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background-color: #1976D2;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (min-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const ImportButton = styled(SyncButton)`
  background-color: #2e7d32;

  &:hover {
    background-color: #1b5e20;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
  justify-content: flex-start;

  @media (min-width: 768px) {
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  @media (min-width: 1024px) {
    gap: 1rem;
    flex-wrap: nowrap;
  }
`;

const DeliveryNoteButton = styled.button`
  padding: 0.4rem 0.6rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 500;
  white-space: nowrap;

  &:hover {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const SelectionCount = styled.span`
  background: white;
  color: #4CAF50;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;

  @media (min-width: 768px) {
    padding: 0.25rem 0.75rem;
    font-size: 0.875rem;
  }
`;

const SelectAllButton = styled.button`
  padding: 0.5rem 0.75rem;
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    background: #e0e0e0;
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    gap: 0.5rem;
  }
`;

const OrderCard = styled.div`
  background: ${props => {
    // Seleccionado siempre tiene fondo verde claro
    if (props.$isSelected) {
      return 'linear-gradient(to right, #e8f5e9 0%, #ffffff 20%)';
    }
    // Urgente = Rojo
    if (props.$isUrgent) {
      return 'linear-gradient(to right, #ffebee 0%, #ffffff 20%)';
    }
    // Profesional = Verde
    if (props.$isProfessional) {
      return 'linear-gradient(to right, #e8f5e9 0%, #ffffff 20%)';
    }
    // Particular = Azul
    return 'linear-gradient(to right, #e3f2fd 0%, #ffffff 20%)';
  }};
  border: ${props => {
    if (props.$isSelected) {
      return '3px solid #4CAF50';
    }
    if (props.$isUrgent) {
      return '2px solid #f44336';
    }
    if (props.$isProfessional) {
      return '1px solid #4CAF50';
    }
    return '1px solid #2196F3';
  }};
  border-radius: 8px;
  margin-bottom: 1.5rem;
  box-shadow: ${props => {
    if (props.$isSelected) return '0 4px 12px rgba(76, 175, 80, 0.3)';
    if (props.$isUrgent) return '0 4px 12px rgba(244, 67, 54, 0.3)';
    return '0 2px 4px rgba(0,0,0,0.05)';
  }};
  overflow: hidden;
  transition: all 0.2s ease;

  ${props => props.$isSelected && `
    transform: translateY(-2px);
  `}
`;

const OrderHeader = styled.div`
  padding: 0.35rem 0.75rem;
  border-bottom: 1px solid ${props => {
    if (props.$isUrgent) return '#ffcdd2';
    if (props.$isProfessional) return '#c8e6c9';
    return '#bbdefb';
  }};
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  background: ${props => props.$isSelected ? 'rgba(76, 175, 80, 0.05)' : 'transparent'};

  @media (min-width: 768px) {
    padding: 0.4rem 1rem;
    flex-wrap: nowrap;
    gap: 0.75rem;
  }
`;

const OrderCheckbox = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 1.25rem;
  transition: all 0.2s ease;
  padding: 0.35rem;
  border-radius: 4px;

  &:hover {
    color: #4CAF50;
    background: rgba(76, 175, 80, 0.1);
    transform: scale(1.1);
  }

  &.selected {
    color: #4CAF50;
    background: rgba(76, 175, 80, 0.15);
    font-weight: bold;
  }

  @media (min-width: 768px) {
    display: flex;
    font-size: 1.5rem;
    padding: 0.5rem;
  }
`;

const OrderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
`;

const OrderLabel = styled.span`
  display: none;
`;

const OrderValue = styled.span`
  font-size: 0.8rem;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 0.85rem;
  }
`;

const OrderNumber = styled.span`
  font-size: 0.95rem;
  color: #007bff;
  font-weight: 600;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.1rem 0.4rem;
  border-radius: 10px;
  font-size: 0.6rem;
  font-weight: 600;
  background-color: ${props => props.$isProfessional ? '#4CAF50' : '#ff9800'};
  color: white;

  @media (min-width: 768px) {
    padding: 0.15rem 0.5rem;
    font-size: 0.65rem;
  }
`;

const OrderNotes = styled.div`
  padding: 0.35rem 1rem;
  background: #fffde7;
  font-size: 0.75rem;
  color: #666;
  font-style: italic;
  border-bottom: 1px solid #fff59d;
`;

const OrderLinesContainer = styled.div`
  padding: 0;
`;

const OrderLine = styled.div`
  padding: ${props => props.$expanded ? '0.75rem 1rem' : '0.4rem 1rem'};
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background: rgba(0,0,0,0.02);
  }

  &:last-child {
    border-bottom: none;
  }
`;

// Compact row - always visible
const LineCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 32px;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 24px 28px minmax(0, 1fr) 45px 70px 120px 28px;
    gap: 0.5rem;
    align-items: center;
  }

  @media (max-width: 1200px) and (min-width: 768px) {
    grid-template-columns: 22px 26px minmax(0, 1fr) 40px 65px 110px 26px;
  }
`;

// Expand button
const ExpandButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 0.75rem;
  transition: all 0.15s;

  &:hover {
    background: rgba(0,0,0,0.05);
    color: #333;
  }

  ${props => props.$expanded && `
    color: #2196F3;
  `}
`;

// Expanded section
const LineExpandedSection = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0 0.25rem 52px;
  border-top: 1px dashed #e0e0e0;
  animation: slideDown 0.15s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 767px) {
    padding-left: 0;
  }
`;

// Status dropdown for compact view
const StatusDropdown = styled.div`
  position: relative;
  display: inline-flex;
`;

const StatusBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.15s;
  background: ${props => props.$color || '#e0e0e0'};
  color: white;
  min-width: 85px;
  justify-content: center;

  &:hover {
    filter: brightness(0.9);
    transform: scale(1.02);
  }

  svg {
    font-size: 0.6rem;
  }

  @media (min-width: 768px) {
    min-width: 100px;
    font-size: 0.72rem;
  }
`;

const StatusDropdownMenu = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  min-width: 140px;
  z-index: 9999;
  overflow: hidden;
`;

const StatusDropdownItem = styled.button`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${props => props.$active ? props.$color + '20' : 'white'};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #333;
  transition: background 0.15s;
  border-left: 3px solid ${props => props.$active ? props.$color : 'transparent'};

  &:hover {
    background: ${props => props.$color}15;
  }

  .status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.$color};
  }
`;

// Keep original for expanded view but smaller
const LineMainRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 35px 2fr 55px 85px 200px 35px;
    gap: 0.5rem;
    align-items: center;
  }

  @media (max-width: 1400px) and (min-width: 768px) {
    grid-template-columns: 30px 1.5fr 50px 80px 180px 30px;
    gap: 0.4rem;
  }

  @media (max-width: 1024px) and (min-width: 768px) {
    grid-template-columns: 28px 1fr 45px 75px 160px 28px;
    gap: 0.35rem;
  }
`;

const LineNumber = styled.div`
  font-weight: 600;
  color: #888;
  font-size: 0.75rem;
  display: none;
  text-align: center;

  @media (min-width: 768px) {
    display: block;
  }
`;

const LineDescription = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ProductName = styled.span`
  font-weight: 500;
  color: #333;
  font-size: 0.95rem;
  line-height: 1.35;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.$compact ? 'unset' : 'unset'};
  -webkit-box-orient: vertical;
  overflow: visible;

  @media (min-width: 768px) {
    font-size: 1.05rem;
    -webkit-line-clamp: ${props => props.$compact ? 2 : 'unset'};
    overflow: ${props => props.$compact ? 'hidden' : 'visible'};
  }

  @media (min-width: 1024px) {
    font-size: 1.1rem;
  }
`;

const LineQuantity = styled.div`
  text-align: center;
  font-weight: 700;
  font-size: 0.95rem;
  color: #333;
  display: none;

  @media (min-width: 768px) {
    display: block;
    font-size: 1.05rem;
  }

  @media (min-width: 1024px) {
    font-size: 1.1rem;
  }
`;

const LinePriceColumn = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;

  .unit-price {
    font-size: 0.65rem;
    color: #888;
    font-weight: 400;
    display: ${props => props.$compact ? 'none' : 'block'};
  }

  .total-price {
    font-size: 0.95rem;
    font-weight: 700;
    color: #333;

    @media (min-width: 768px) {
      font-size: 1.05rem;
    }

    @media (min-width: 1024px) {
      font-size: 1.1rem;
    }
  }

  @media (min-width: 768px) {
    .unit-price {
      font-size: 0.68rem;
    }
    .total-price {
      font-size: 0.85rem;
    }
  }
`;

const StatusButtonsContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 0.25rem;
  width: auto;

  @media (min-width: 768px) {
    gap: 0.35rem;
  }

`;

const StatusButton = styled.button`
  min-width: 28px;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 0.65rem;
  font-weight: 700;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  background: ${props => props.$active ? props.$color : '#e0e0e0'};
  color: ${props => props.$active ? 'white' : props.$color};

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 768px) {
    height: 32px;
    border-radius: 6px;
    font-size: 0.75rem;
  }

  @media (max-width: 1024px) and (min-width: 768px) {
    height: 30px;
    font-size: 0.7rem;
  }
`;

const LineNotesRow = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-left: 3px solid #2196F3;
  border-radius: 4px;
`;

const LineNoteText = styled.div`
  font-size: 0.875rem;
  color: #555;
  font-style: italic;
  line-height: 1.5;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  &::before {
    content: '📝';
    flex-shrink: 0;
  }
`;

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: rgba(0,0,0,0.05);
    color: #333;
  }
`;

const DropdownMenu = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  min-width: 160px;
  z-index: 9999;
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: white;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #333;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    color: #666;
  }
`;

const MenuWrapper = styled.div`
  position: relative;
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: #b52a38;
  }

  @media (min-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const RestoreButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: #218838;
  }

  @media (min-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const OrderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;

  @media (min-width: 768px) {
    gap: 0.5rem;
  }
`;

const ViewButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: #5a6268;
  }

  @media (min-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.75rem;
  }
`;

const OrderFooter = styled.div`
  padding: 0.4rem 0.75rem;
  background: rgba(0,0,0,0.02);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 2px solid ${props => props.$isProfessional ? '#c8e6c9' : '#ffe0b2'};

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
  }
`;

const TotalSection = styled.div`
  display: flex;
  align-items: center;
`;

const TotalLabel = styled.span`
  display: none;
`;

const TotalAmount = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: baseline;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin: 2rem 0;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #2196F3;
  background: ${props => props.$active ? '#2196F3' : props.disabled ? '#f0f0f0' : 'white'};
  color: ${props => props.$active ? 'white' : props.disabled ? '#999' : '#2196F3'};
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#1976D2' : props.disabled ? '#f0f0f0' : '#e3f2fd'};
  }
`;

const PaginationInfo = styled.span`
  font-size: 0.875rem;
  color: #666;
`;

const StatusModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const StatusModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  min-width: 300px;
`;

const StatusOption = styled.button`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:hover {
    background: #f5f5f5;
  }
`;

const StatusDotLarge = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

// Images Modal styles
const ImagesModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ImagesModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  width: 95vw;

  @media (min-width: 768px) {
    padding: 2rem;
    width: auto;
    min-width: 700px;
    max-width: 1100px;
  }
`;

const ImagesModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
`;

const ImagesModalTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.1rem;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ImagesModalClose = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.25rem;

  &:hover {
    color: #333;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
  }
`;

const ImageCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
  border: 1px solid #e0e0e0;
`;

const ProductImageLarge = styled.img`
  width: 100%;
  height: 200px;
  object-fit: contain;
  border-radius: 4px;
  background: white;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }

  @media (min-width: 768px) {
    height: 220px;
  }
`;

const ProductImagePlaceholder = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 4px;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    height: 220px;
  }
`;

const ModalProductName = styled.div`
  font-size: 0.75rem;
  color: #333;
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const NoImagesMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
  font-size: 0.9rem;
`;

const WorkOrderSelectorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const WorkOrderSelectorContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  min-width: 320px;
  max-width: 400px;

  h3 {
    margin: 0 0 1rem 0;
    color: #333;
    font-size: 1.1rem;
  }
`;

const WorkOrderOption = styled.button`
  width: 100%;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 2px solid ${props => props.$selected ? '#6f42c1' : '#ddd'};
  background: ${props => props.$selected ? '#f3f0ff' : 'white'};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    border-color: #6f42c1;
    background: #f8f6ff;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .option-title {
    font-weight: 600;
    font-size: 1rem;
    color: #333;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .option-description {
    font-size: 0.85rem;
    color: #666;
  }
`;

const WorkOrderModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eee;

  button {
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
  }
`;

const CancelButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  color: #666;
  margin-right: 0.5rem;

  &:hover {
    background: #f5f5f5;
  }
`;

// Status configuration with initials and colors
const STATUS_CONFIG = [
  { key: 'pending', initial: 'P', color: '#ffc107', label: 'Pendiente' },
  { key: 'confirmed', initial: 'C', color: '#007bff', label: 'Confirmado' },
  { key: 'reviewed', initial: 'R', color: '#17a2b8', label: 'Revisado' },
  { key: 'out_of_stock', initial: 'S', color: '#dc3545', label: 'Sin Stock', special: true },
  { key: 'cut', initial: 'Co', color: '#fd7e14', label: 'Cortado' },
  { key: 'stapled', initial: 'G', color: '#e83e8c', label: 'Grapado' },
  { key: 'finished', initial: 'E', color: '#6f42c1', label: 'Enmarcado' },
  { key: 'ready', initial: 'L', color: '#20c997', label: 'Listo' },
  { key: 'delivered', initial: 'En', color: '#17a2b8', label: 'Entregado' },
  { key: 'invoiced', initial: 'F', color: '#28a745', label: 'Facturado', readonly: true }
];

const ITEMS_PER_PAGE = 50;

// Component to load and display product images for an order
const ImagesModal = ({ orderId, orderNumber, onClose }) => {
  const [loading, setLoading] = React.useState(true);
  const [products, setProducts] = React.useState([]);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadOrderImages = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/orders/${orderId}`);
        const order = response.data.order;

        // Get unique products from order lines
        const productsMap = new Map();
        (order.lines || []).forEach(line => {
          if (line.product && !productsMap.has(line.product.id)) {
            productsMap.set(line.product.id, line.product);
          }
        });

        setProducts(Array.from(productsMap.values()));
      } catch (err) {
        console.error('Error loading order images:', err);
        setError('Error al cargar las imágenes');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      loadOrderImages();
    }
  }, [orderId]);

  return (
    <ImagesModalOverlay onClick={onClose}>
      <ImagesModalContent onClick={(e) => e.stopPropagation()}>
        <ImagesModalHeader>
          <ImagesModalTitle>
            Materiales - Pedido #{orderNumber}
          </ImagesModalTitle>
          <ImagesModalClose onClick={onClose}>
            <FaTimes />
          </ImagesModalClose>
        </ImagesModalHeader>

        {loading ? (
          <NoImagesMessage>Cargando imágenes...</NoImagesMessage>
        ) : error ? (
          <NoImagesMessage>{error}</NoImagesMessage>
        ) : products.length === 0 ? (
          <NoImagesMessage>No hay productos asociados a este pedido</NoImagesMessage>
        ) : (
          <ImagesGrid>
            {products.map(product => (
              <ImageCard key={product.id}>
                {product.image ? (
                  <ProductImageLarge
                    src={product.image}
                    alt={product.name}
                    onClick={() => window.open(product.image, '_blank')}
                    title="Clic para ver imagen completa"
                  />
                ) : (
                  <ProductImagePlaceholder>Sin imagen</ProductImagePlaceholder>
                )}
                <ModalProductName title={product.name}>{product.name}</ModalProductName>
              </ImageCard>
            ))}
          </ImagesGrid>
        )}
      </ImagesModalContent>
    </ImagesModalOverlay>
  );
};

const Orders = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending'); // 'all', 'pending', 'completed', or 'archived'
  const [lineStatusFilter, setLineStatusFilter] = useState(null); // Filter by specific line status
  const [sourceFilter, setSourceFilter] = useState(null); // 'pro', 'tpv', or null for all
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncingOrderId, setSyncingOrderId] = useState(null);
  const [isImportingOdoo, setIsImportingOdoo] = useState(false);
  const [updatingFingerprints, setUpdatingFingerprints] = useState(() => new Set());
  const [openMenu, setOpenMenu] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [outOfStockModal, setOutOfStockModal] = useState(null);
  const [notesModal, setNotesModal] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedLines, setExpandedLines] = useState(new Set());
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [calculatorModal, setCalculatorModal] = useState(null);
  const [imagesModal, setImagesModal] = useState(null); // { order, lines }
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const debounceRef = useRef(null);

  // Debounce search term (300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm]);

  // Server-side paginated query
  const queryParams = filterStatus === 'deleted'
    ? { limit: ITEMS_PER_PAGE, offset: (currentPage - 1) * ITEMS_PER_PAGE, deleted: 'true', search: debouncedSearch || undefined }
    : { limit: ITEMS_PER_PAGE, offset: (currentPage - 1) * ITEMS_PER_PAGE, view: filterStatus, search: debouncedSearch || undefined };

  const { data: ordersData, isLoading, error } = useQuery({
    queryKey: ['orders', filterStatus, currentPage, debouncedSearch],
    queryFn: () => filterStatus === 'deleted'
      ? orderService.getDeleted(queryParams)
      : orderService.getAll(queryParams),
    placeholderData: (prev) => prev,
  });
  const orders = ordersData?.orders;
  const totalOrders = ordersData?.total || 0;

  // Tab counts (lightweight, cached)
  const { data: tabCounts } = useQuery({
    queryKey: ['orders-counts', debouncedSearch],
    queryFn: () => orderService.getCounts({ search: debouncedSearch || undefined }),
    staleTime: 30000,
  });

  const ordersCount = tabCounts?.all || 0;
  const deletedOrdersCount = tabCounts?.deleted || 0;

  // Helper function to round to 5 cents (for TPV orders)
  const roundToFiveCents = (number) => {
    const scaled = number * 20;
    const nearest = Math.round(scaled);
    if (Math.abs(scaled - nearest) < 0.01) return nearest / 20;
    return Math.ceil(scaled) / 20;
  };

  // Helper to determine if order was created with Calculadora Profesional
  const isCalculadoraPro = (order) => {
    if (!order) return false;
    // Use orderType field if available, fallback to fingerprint pattern
    if (order.orderType) return order.orderType === 'PRO';
    const fp = order.fingerprint || '';
    return fp.startsWith('INT-') || fp.startsWith('EXT-');
  };

  // Helper to calculate final amount with correct rounding based on order type
  const calculateDisplayFinalAmount = (order) => {
    if (!order) return 0;
    const finalAmount = parseFloat(order.finalAmount || order.totalAmount || 0);

    // If manual price is set, always use it exactly (no rounding)
    if (order.metadata?.isManualPrice && order.metadata?.manualFinalAmount) {
      return parseFloat(order.metadata.manualFinalAmount);
    }

    // For TPV orders, apply 5-cent rounding to final amount WITH VAT
    // For CalculadoraPro orders, use finalAmount as-is (base already rounded)
    if (!isCalculadoraPro(order)) {
      // TPV order - round final amount to 5 cents
      return roundToFiveCents(finalAmount);
    }

    // CalculadoraPro order - use as-is
    return finalAmount;
  };

  // Helper to calculate base amount (subtotal without VAT) with correct rounding
  // For TPV orders: calculates base from rounded final (169.23€ → 169.25€ → base 139.88€)
  // For CalculadoraPro: uses totalAmount as-is (base already correctly rounded)
  const calculateDisplayBaseAmount = (order) => {
    if (!order) return 0;

    // CalculadoraPro orders - use totalAmount as-is (base already rounded correctly)
    if (isCalculadoraPro(order)) {
      return parseFloat(order.totalAmount || 0);
    }

    // TPV orders - calculate base from rounded final amount
    // This ensures base matches when final amount is rounded to 5 cents
    const roundedFinalAmount = calculateDisplayFinalAmount(order);
    const baseAmount = roundedFinalAmount / 1.21;

    return baseAmount;
  };

  useEffect(() => {
    if (error) {
      toast.error('Error al cargar los pedidos');
    }
  }, [error]);

  // Clear selection when changing tabs
  useEffect(() => {
    setSelectedOrders([]);
    setLineStatusFilter(null); // Clear line status filter when changing tabs
  }, [filterStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, debouncedSearch, lineStatusFilter, sourceFilter]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      // Handle menu closing
      if (openMenu) {
        const wrapper = event.target.closest('[data-menu-wrapper]');
        if (!wrapper || wrapper.getAttribute('data-menu-wrapper') !== openMenu) {
          setOpenMenu(null);
        }
      }

      // Handle status dropdown closing
      if (openStatusDropdown) {
        const statusWrapper = event.target.closest('[data-status-dropdown]');
        if (!statusWrapper || statusWrapper.getAttribute('data-status-dropdown') !== openStatusDropdown) {
          setOpenStatusDropdown(null);
        }
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setOpenStatusDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleGlobalClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openMenu, openStatusDropdown]);

  const invalidateOrderQueries = (orderIds) => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['orders-counts'] });

    if (!orderIds) {
      return;
    }

    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
    const uniqueIds = Array.from(new Set(
      ids
        .map(id => (id !== undefined && id !== null ? String(id) : null))
        .filter(Boolean)
    ));

    uniqueIds.forEach((id) => {
      queryClient.invalidateQueries({ queryKey: ['order', id] });
    });
  };

  const applyLineStatusToCache = (lineIds, status) => {
    const ids = Array.isArray(lineIds) ? lineIds : [lineIds];
    const idSet = new Set(ids.map((id) => String(id)));

    const patchOrders = (existing) => {
      if (!Array.isArray(existing)) return existing;
      return existing.map((order) => {
        if (!Array.isArray(order?.lines)) return order;
        return {
          ...order,
          lines: order.lines.map((line) => (idSet.has(String(line.id)) ? { ...line, status } : line))
        };
      });
    };

    queryClient.invalidateQueries({ queryKey: ['orders'] }); queryClient.invalidateQueries({ queryKey: ['orders-counts'] });
    // (invalidated above)
  };

  const refreshOrderCache = async (orderId) => {
    if (!orderId) return;
    const stringId = String(orderId);

    try {
      const updatedOrder = await orderService.getById(orderId);
      if (!updatedOrder) {
        return;
      }

      queryClient.setQueryData(['order', stringId], updatedOrder);

      queryClient.setQueryData(['orders', filterStatus, currentPage, debouncedSearch], (existing) => {
        if (!existing || !Array.isArray(existing.orders)) {
          return existing;
        }

        const index = existing.orders.findIndex(order => String(order.id) === stringId);
        if (index === -1) {
          return existing;
        }

        const nextOrders = [...existing.orders];
        nextOrders[index] = updatedOrder;
        return { ...existing, orders: nextOrders };
      });
    } catch (cacheError) {
      console.error('Failed to refresh order cache', cacheError);
    }
  };

  const updateLineStatusMutation = useMutation({
    mutationFn: async ({ lineId, status, orderId }) => {
      await api.put(`/orders/lines/${lineId}/status`, { status });

      if (orderId) {
        await orderService.updateStatus(orderId, status);
      }

      return { orderId };
    },
    onSuccess: (result) => {
      invalidateOrderQueries(result?.orderId);
      toast.success('Estado actualizado');
      setStatusModal(null);
    },
    onError: () => {
      toast.error('Error al actualizar el estado');
    },
  });

  const toggleImportantMutation = useMutation({
    mutationFn: ({ orderId, isImportant }) => orderService.toggleImportant(orderId, isImportant),
    onSuccess: (data, variables) => {
      invalidateOrderQueries(variables.orderId);
      toast.success(data.message || 'Estado actualizado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al actualizar');
    },
  });

  const handleToggleImportant = (order, e) => {
    e.stopPropagation(); // Prevent card click
    toggleImportantMutation.mutate({
      orderId: order.id,
      isImportant: !order.isImportant
    });
  };

  const handleSyncOdoo = async () => {
    setIsSyncing(true);
    try {
      const response = await api.post('/odoo-sync/sync-all-orders');
      if (response.data.success) {
        const stats = response.data.stats;
        if (stats.totalSynced > 0) {
          toast.success(`✅ ${stats.totalSynced} pedidos sincronizados`);
          queryClient.invalidateQueries({ queryKey: ['orders'] });
        } else {
          toast.info('No hay pedidos pendientes de sincronizar');
        }
      }
    } catch (error) {
      toast.error(`Error de sincronización: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // Sync individual order to Odoo (for TPV orders that need manual sync)
  const handleSyncSingleOrderToOdoo = async (orderId, orderNumber, e) => {
    e.stopPropagation();
    if (syncingOrderId) return; // Prevent concurrent syncs
    setSyncingOrderId(orderId);
    try {
      const response = await api.post(`/orders/${orderId}/sync-odoo`);
      if (response.data.success) {
        toast.success(`✅ Pedido #${orderNumber} sincronizado a Odoo`);
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      } else {
        toast.error(`Error: ${response.data.error || 'Error desconocido'}`);
      }
    } catch (error) {
      toast.error(`Error de sincronización: ${error.message}`);
    } finally {
      setSyncingOrderId(null);
    }
  };

  const handleImportOrdersFromOdoo = async () => {
    const ok = window.confirm('¿Importar pedidos desde Odoo (m2) a la base de datos local?');
    if (!ok) return;

    setIsImportingOdoo(true);
    try {
      const [saleResponse, posResponse] = await Promise.all([
        api.post('/odoo-sync/import-sale-orders', {
          limit: 500,
          offset: 0,
          states: ['sale', 'done'],
          since: null,
          updateExisting: true
        }),
        api.post('/odoo-sync/import-pos-orders', {
          limit: 500,
          offset: 0,
          states: ['paid', 'done'],
          since: null,
          updateExisting: true
        })
      ]);

      const saleStats = saleResponse.data?.stats;
      const posStats = posResponse.data?.stats;
      const created = (saleStats?.created || 0) + (posStats?.created || 0);
      const updated = (saleStats?.updated || 0) + (posStats?.updated || 0);
      const errors = (saleStats?.errors?.length || 0) + (posStats?.errors?.length || 0);

      if (saleResponse.data?.success && posResponse.data?.success) {
        toast.success(`Importados Odoo → Local: +${created} creados, ${updated} actualizados${errors ? ` (${errors} errores)` : ''}`);
        queryClient.invalidateQueries({ queryKey: ['orders'] });
      } else {
        toast.error('No se pudo importar desde Odoo');
      }
    } catch (error) {
      toast.error(`Error importando desde Odoo: ${error.response?.data?.details || error.message}`);
    } finally {
      setIsImportingOdoo(false);
    }
  };

  const handleToggleOrderSelection = (order) => {
    setSelectedOrders(prev => {
      const isSelected = prev.some(o => o.id === order.id);
      if (isSelected) {
        return prev.filter(o => o.id !== order.id);
      } else {
        return [...prev, order];
      }
    });
  };

  const handleSelectAll = () => {
    // Select all orders from the currently active tab only
    const ordersToSelect = filteredOrders.filter(order => !order.isArchived);

    if (ordersToSelect.length === 0) {
      let message = 'No hay pedidos en esta pestaña';
      if (filterStatus === 'pending') {
        message = 'No hay pedidos pendientes de realizar';
      } else if (filterStatus === 'completed') {
        message = 'No hay pedidos ya realizados';
      } else if (filterStatus === 'archived') {
        message = 'No hay pedidos archivados';
      } else if (filterStatus === 'all') {
        message = 'No hay pedidos';
      }
      toast.info(message);
      return;
    }

    setSelectedOrders(ordersToSelect);
    toast.success(`${ordersToSelect.length} pedido(s) seleccionado(s)`);
  };

  const handleDeselectAll = () => {
    setSelectedOrders([]);
  };

  const handleCreateDeliveryNotes = () => {
    if (selectedOrders.length === 0) {
      toast.warning('Selecciona al menos un pedido');
      return;
    }
    setShowDeliveryNoteModal(true);
  };

  // Select all pending orders (for work orders)
  const handleSelectAllPending = () => {
    const pendingOrders = filteredOrders.filter(order =>
      !['delivered', 'invoiced'].includes(order.status) && !order.isArchived
    );

    if (pendingOrders.length === 0) {
      toast.info('No hay pedidos pendientes para seleccionar');
      return;
    }

    setSelectedOrders(pendingOrders);
    toast.success(`${pendingOrders.length} pedido(s) seleccionado(s)`);
  };

  // Work order type selector
  const [showWorkOrderSelector, setShowWorkOrderSelector] = useState(false);

  // Show work order type selector
  const handleCreateCombinedWorkOrder = () => {
    if (selectedOrders.length === 0) {
      toast.warning('Selecciona al menos un pedido');
      return;
    }
    setShowWorkOrderSelector(true);
  };

  // Generate work order PDF with selected type
  const handleGenerateWorkOrder = async (templateType) => {
    setShowWorkOrderSelector(false);

    try {
      const orderIds = selectedOrders.map(o => o.id);
      const response = await api.post('/orders/work-order/combined', {
        orderIds,
        templateType // 'detailed' or 'simple'
      }, {
        responseType: 'blob'
      });

      // Download PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orden-taller-${selectedOrders.map(o => o.orderNumber).join('-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Orden de taller generada para ${selectedOrders.length} pedido(s)`);
      setSelectedOrders([]);
    } catch (error) {
      console.error('Error generating work order:', error);
      toast.error('Error al generar la orden de taller');
    }
  };

  const handleCreateInvoices = async () => {
    if (selectedOrders.length === 0) {
      toast.warning('Selecciona al menos un pedido entregado');
      return;
    }

    try {
      // Get all delivery notes
      const allDeliveryNotes = await deliveryNoteService.getAll();

      // Find delivery notes for selected orders
      const orderIds = selectedOrders.map(o => o.id);
      const relevantDeliveryNotes = allDeliveryNotes.filter(dn =>
        dn.lines?.some(line => orderIds.includes(line.orderId))
      );

      if (relevantDeliveryNotes.length === 0) {
        toast.error('No se encontraron albaranes para los pedidos seleccionados');
        return;
      }

      // Get all delivery note line IDs that are not invoiced yet
      const deliveryLineIds = [];
      relevantDeliveryNotes.forEach(dn => {
        dn.lines?.forEach(line => {
          if (!line.isInvoiced && orderIds.includes(line.orderId)) {
            deliveryLineIds.push(line.id);
          }
        });
      });

      if (deliveryLineIds.length === 0) {
        toast.error('Todas las líneas de los pedidos seleccionados ya están facturadas');
        return;
      }

      toast.info('Creando facturas...');

      // Create invoices from delivery lines
      const result = await invoiceService.createFromDeliveryLines(deliveryLineIds);

      toast.success(result.message || 'Facturas creadas correctamente');

      // Refresh orders and order detail views
      invalidateOrderQueries(orderIds);

      // Clear selection
      setSelectedOrders([]);

      // Navigate to invoices page
      navigate('/invoices');

    } catch (error) {
      console.error('Error creating invoices:', error);
      toast.error(error.response?.data?.error || 'Error al crear las facturas');
    }
  };

  const handleStatusClick = (line) => {
    setStatusModal(line);
  };

  const handleStatusChange = (newStatus) => {
    if (statusModal) {
      updateLineStatusMutation.mutate({
        lineId: statusModal.id,
        status: newStatus,
        orderId: statusModal.orderId
      });
    }
  };

  const handleQuickStatusChange = async (lineId, newStatus, components, fingerprint) => {
    // If status is out_of_stock, open modal to select which components are missing
    if (newStatus === 'out_of_stock') {
      setOutOfStockModal({ lineId, components, fingerprint });
      return;
    } else if (newStatus === 'ready' || newStatus === 'delivered') {
      if (updatingFingerprints.has(fingerprint)) return;
      setUpdatingFingerprints((prev) => {
        const next = new Set(prev);
        next.add(fingerprint);
        return next;
      });

      // Show confirmation when marking as ready/delivered
      const statusLabel = newStatus === 'ready' ? 'LISTO' : 'ENTREGADO';
      const message = `¿Marcar toda esta línea como ${statusLabel}?${components.length > 1 ? ` (${components.length} componentes)` : ''}`;

      if (window.confirm(message)) {
        // Update ALL components of this line (same fingerprint)
        try {
          await Promise.all(
            components.map(component =>
              api.put(`/orders/lines/${component.id}/status`, { status: newStatus })
            )
          );

          applyLineStatusToCache(components.map((c) => c.id), newStatus);
          const orderId = components[0]?.orderId;
          if (orderId) {
            invalidateOrderQueries(orderId);
          } else {
            invalidateOrderQueries(components.map(component => component.orderId));
          }

          toast.success(`Línea marcada como ${statusLabel}`);
        } catch (error) {
          console.error('Error updating status:', error);
          toast.error('Error al actualizar el estado');
        } finally {
          setUpdatingFingerprints((prev) => {
            const next = new Set(prev);
            next.delete(fingerprint);
            return next;
          });
        }
      } else {
        setUpdatingFingerprints((prev) => {
          const next = new Set(prev);
          next.delete(fingerprint);
          return next;
        });
      }
    } else {
      if (updatingFingerprints.has(fingerprint)) return;
      setUpdatingFingerprints((prev) => {
        const next = new Set(prev);
        next.add(fingerprint);
        return next;
      });

      // For other statuses, update ALL components of the line
      try {
        await Promise.all(
          components.map(component =>
            api.put(`/orders/lines/${component.id}/status`, { status: newStatus })
          )
        );
        applyLineStatusToCache(components.map((c) => c.id), newStatus);
        const orderId = components[0]?.orderId;
        if (orderId) {
          invalidateOrderQueries(orderId);
        } else {
          invalidateOrderQueries(components.map(component => component.orderId));
        }
        toast.success('Estado actualizado');
      } catch (error) {
        console.error('Error updating status:', error);
        toast.error('Error al actualizar el estado');
      } finally {
        setUpdatingFingerprints((prev) => {
          const next = new Set(prev);
          next.delete(fingerprint);
          return next;
        });
      }
    }
  };

  const handleSaveOutOfStock = async (selectedComponents) => {
    if (!outOfStockModal) return;

    try {
      // Update status and save which components are out of stock
      await api.put(`/orders/lines/${outOfStockModal.lineId}/status`, {
        status: 'out_of_stock'
      });

      // Save the out of stock components in metadata
      await api.put(`/orders/${outOfStockModal.components[0].orderId}/lines/${outOfStockModal.lineId}`, {
        metadata: {
          outOfStockComponents: selectedComponents
        }
      });

      const orderId = outOfStockModal.components[0]?.orderId;
      if (orderId) {
        invalidateOrderQueries(orderId);
      } else {
        invalidateOrderQueries(outOfStockModal.components.map(component => component.orderId));
      }
      toast.success(`${selectedComponents.length} componente(s) marcado(s) sin stock`);
      setOutOfStockModal(null);
    } catch (error) {
      console.error('Error saving out of stock components:', error);
      toast.error('Error al guardar componentes sin stock');
    }
  };

  const handleEditLine = (fingerprint, components, order) => {
    const firstComponent = components[0];

    // Get original dimensions from metadata
    const metadata = typeof firstComponent.metadata === 'string'
      ? JSON.parse(firstComponent.metadata || '{}')
      : (firstComponent.metadata || {});

    const hasOriginalDimensions = metadata.originalWidth && metadata.originalHeight;

    // Build article object similar to groupLinesIntoArticles in OrderDetail
    const article = {
      id: fingerprint,
      lines: components,
      quantity: firstComponent.quantity || 1,
      dimensions: hasOriginalDimensions
        ? `${metadata.originalWidth}x${metadata.originalHeight}`
        : `${firstComponent.width}x${firstComponent.height}`,
      originalWidth: hasOriginalDimensions ? metadata.originalWidth : firstComponent.width,
      originalHeight: hasOriginalDimensions ? metadata.originalHeight : firstComponent.height,
      originalMeasurementType: hasOriginalDimensions ? (metadata.originalMeasurementType || 'INT') : 'EXT',
      totalAmount: components.reduce((sum, c) => sum + parseFloat(c.totalPrice || 0), 0),
      fingerprint: fingerprint,
      notes: firstComponent.notes || '', // Include line notes for editing
      discountPercentage: order.discountPercentage || 0
    };

    setCalculatorModal({ article, order });
    setOpenMenu(null);
  };

  const handleSaveLine = async (updatedLine) => {
    try {
      const { orderId, components, width, height, quantity, notes } = updatedLine;

      // Update all components (order lines) with the same fingerprint
      const updatePromises = components.map(component =>
        api.put(`/orders/${orderId}/lines/${component.id}`, {
          productId: component.productId,
          width: parseFloat(width) || component.width,
          height: parseFloat(height) || component.height,
          quantity: parseInt(quantity) || component.quantity,
          notes: notes || '',
          totalPrice: parseFloat(component.totalPrice || 0),
          unitPrice: parseFloat(component.unitPrice || 0),
          metadata: component.metadata || {}
        })
      );

      await Promise.all(updatePromises);

      toast.success('Línea actualizada correctamente');
      setEditModal(null);
      await refreshOrderCache(orderId);
      invalidateOrderQueries(orderId);
    } catch (error) {
      console.error('Error updating line:', error);
      toast.error('Error al actualizar la línea');
    }
  };

  // Generate workshop PDF via backend (same format as multi-select simple PDF)
  const generateWorkshopPDF = async (order) => {
    try {
      const response = await api.post('/orders/work-order/combined', {
        orderIds: [order.id],
        templateType: 'simple'
      }, {
        responseType: 'blob'
      });

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `taller-${order.orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating workshop PDF:', error);
      toast.error('Error al generar el PDF de taller');
    }
  };

  // Handle save from Calculator modal
  const handleCalculatorEditSave = async (editResult) => {
    if (!calculatorModal) return;

    try {
      const { article, order } = calculatorModal;

      if (!editResult || !editResult.calculation) {
        toast.error('No hay datos de cálculo para guardar');
        return;
      }

      const calculation = editResult.calculation;
      const originalLines = article.lines || [];
      const calculationItems = calculation.items || editResult.items || [];
      const materials = editResult.materials || {};
      const isProOrder = isCalculadoraPro(order);
      const vatMultiplier = 1.21;

      console.log('=== handleCalculatorEditSave DEBUG ===');
      console.log('originalLines:', originalLines.map(l => ({ id: l.id, lineType: l.lineType, productId: l.productId })));
      console.log('calculationItems:', calculationItems.map(i => ({ type: i.type, typeWithIndex: i.typeWithIndex, productId: i.productId, totalPrice: i.totalPrice })));
      console.log('materials:', materials);

      // The calculation returns the EXTERIOR dimensions
      const calculatedWidth = calculation.width || editResult.formData.width;
      const calculatedHeight = calculation.height || editResult.formData.height;

      // Check if manual price is enabled
      const isManualPrice = editResult.formData.isManualPrice;
      const manualTotal = editResult.formData.manualPrice;

      // Calculate the adjustment factor for manual price
      let priceAdjustmentFactor = 1;
      if (isManualPrice && manualTotal !== null && calculation.originalTotal) {
        priceAdjustmentFactor = manualTotal / calculation.originalTotal;
      } else if (isManualPrice && manualTotal !== null && calculation.total) {
        // If no originalTotal, use the total as reference
        const calcTotal = calculationItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
        if (calcTotal > 0) {
          priceAdjustmentFactor = manualTotal / calcTotal;
        }
      }

      // Update discount and manual price metadata BEFORE updating lines
      // so the backend recalculation uses the correct discount percentage
      const newDiscount = parseFloat(editResult.formData.discount) || 0;
      try {
        const patchData = {
          discountPercentage: newDiscount,
          // El precio manual se aplica a la LÍNEA editada (vía isPriceManual + factor de ajuste),
          // NO al total del pedido. Limpiamos siempre manualFinalAmount para que el total del pedido
          // sea la SUMA de todas las líneas. (Antes, editar una línea de un pedido con varias líneas
          // ponía el total del pedido = precio de esa única línea.) El backend recalcula desde las líneas.
          metadata: { manualFinalAmount: null, isManualPrice: false }
        };
        await api.patch(`/orders/${order.id}`, patchData);
        console.log('Updated order discount and manual price:', patchData);
      } catch (metaError) {
        console.error('Failed to update order metadata/discount:', metaError);
      }

      // Create tracking sets
      const usedItemIndices = new Set();
      const matchedOriginalLineIds = new Set();

      // Helper function to get base type from lineType
      const getBaseType = (lineType) => {
        if (!lineType) return 'other';
        if (lineType === 'moldura') return 'moldura';
        if (lineType.includes('passepartout')) return 'passepartout';
        if (lineType.includes('cristal')) return 'cristal';
        if (lineType.includes('trasera')) return 'trasera';
        if (lineType.includes('accesorio')) return 'accesorio';
        return 'other';
      };

      // Helper to get index within type (e.g., passepartout1 -> 0, passepartout2 -> 1)
      const getTypeIndex = (lineType) => {
        const match = lineType?.match(/(\d+)$/);
        return match ? parseInt(match[1]) - 1 : 0;
      };

      // Group calculation items by type for better matching
      const itemsByType = {};
      calculationItems.forEach((item, idx) => {
        const type = item.type || 'other';
        if (!itemsByType[type]) {
          itemsByType[type] = [];
        }
        itemsByType[type].push({ ...item, originalIndex: idx });
      });

      // 1. Process each original line and find matching updated item
      for (const originalLine of originalLines) {
        if (!originalLine.id) continue;

        const baseType = getBaseType(originalLine.lineType);
        const typeIndex = getTypeIndex(originalLine.lineType);

        // Find matching item from calculation by type and index
        let matchingItem = null;
        const typeItems = itemsByType[baseType] || [];
        if (typeItems[typeIndex] && !usedItemIndices.has(typeItems[typeIndex].originalIndex)) {
          matchingItem = typeItems[typeIndex];
          usedItemIndices.add(typeItems[typeIndex].originalIndex);
        } else {
          // Fallback: find first unused item of same type
          for (const item of typeItems) {
            if (!usedItemIndices.has(item.originalIndex)) {
              matchingItem = item;
              usedItemIndices.add(item.originalIndex);
              break;
            }
          }
        }

        if (!matchingItem) {
          // No matching calculation item - this component was removed, skip (will be deleted below)
          console.log(`  → No match for originalLine ${originalLine.id} (${originalLine.lineType}) - will be deleted`);
          continue;
        }

        // Mark this line as matched
        matchedOriginalLineIds.add(originalLine.id);
        console.log(`  → Matched originalLine ${originalLine.id} (${originalLine.lineType}) to item index ${matchingItem.originalIndex}`);

        const itemWidth = matchingItem.width || calculatedWidth;
        const itemHeight = matchingItem.height || calculatedHeight;

        const existingMeta = typeof originalLine.metadata === 'string'
          ? JSON.parse(originalLine.metadata || '{}')
          : (originalLine.metadata || {});

        const updateData = {
          quantity: editResult.formData.units,
          width: itemWidth,
          height: itemHeight,
          notes: editResult.formData.notes || '',
          metadata: {
            ...existingMeta,
            originalWidth: editResult.formData.width,
            originalHeight: editResult.formData.height,
            originalMeasurementType: editResult.formData.measurementType,
            isManualPrice: isManualPrice || false,
            customDescription: editResult.formData.description || null
          }
        };

        // Update price from calculation
        // matchingItem.totalPrice is the price for 1 unit (from calculator)
        const displayedPriceSingle = matchingItem.totalPrice || 0;
        const adjustedDisplayedPriceSingle = isManualPrice ? displayedPriceSingle * priceAdjustmentFactor : displayedPriceSingle;
        const adjustedBasePriceSingle = isProOrder ? adjustedDisplayedPriceSingle : (adjustedDisplayedPriceSingle / vatMultiplier);
        const units = editResult.formData.units || 1;
        updateData.unitPrice = adjustedBasePriceSingle;
        updateData.totalPrice = adjustedBasePriceSingle * units;

        // Update productId if available
        if (matchingItem.productId) {
          updateData.productId = matchingItem.productId;
        }

        // Also check materials for productId if not found in matchingItem
        if (!updateData.productId) {
          const materialsKey = baseType === 'moldura' ? 'molduras' :
                              baseType === 'passepartout' ? 'passepartouts' :
                              baseType === 'cristal' ? 'cristales' :
                              baseType === 'trasera' ? 'traseras' :
                              baseType === 'accesorio' ? 'accesorios' : null;

          if (materialsKey && materials[materialsKey]) {
            const materialItem = materials[materialsKey][typeIndex];
            if (materialItem) {
              const productId = typeof materialItem === 'object' ? materialItem.productId : materialItem;
              if (productId) {
                updateData.productId = productId;
              }
            }
          }
        }

        try {
          await api.put(`/orders/${order.id}/lines/${originalLine.id}`, updateData);
          console.log(`  → PUT success for line ${originalLine.id}`);
        } catch (putError) {
          console.error(`  → PUT FAILED for line ${originalLine.id}:`, putError.response?.data || putError.message);
        }
      }

      // 2. Delete original lines that were removed (no matching calculation item)
      console.log('Step 2: Delete unmatched original lines');
      console.log('  matchedOriginalLineIds:', [...matchedOriginalLineIds]);
      for (const originalLine of originalLines) {
        if (originalLine.id && !matchedOriginalLineIds.has(originalLine.id)) {
          console.log(`  → Deleting line ${originalLine.id} (${originalLine.lineType})`);
          await api.delete(`/orders/${order.id}/lines/${originalLine.id}`);
        }
      }

      // 3. Create new lines for calculation items that were added (not matched to any existing line)
      const articleFingerprint = originalLines[0]?.metadata?.fingerprint || article.fingerprint;
      console.log('Step 3: Create new lines');
      console.log('  usedItemIndices:', [...usedItemIndices]);
      console.log('  articleFingerprint:', articleFingerprint);
      for (const [type, items] of Object.entries(itemsByType)) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (usedItemIndices.has(item.originalIndex)) {
            console.log(`  → Skipping ${type} item (index ${item.originalIndex}) - already matched`);
            continue; // Already matched to an existing line
          }

          // Determine lineType with index
          const lineType = type === 'moldura' ? 'moldura' : (item.typeWithIndex || `${type}${i + 1}`);

          // Get productId from calculation item or materials
          let productId = item.productId;
          if (!productId) {
            const materialsKey = type === 'moldura' ? 'molduras' :
                                type === 'passepartout' ? 'passepartouts' :
                                type === 'cristal' ? 'cristales' :
                                type === 'trasera' ? 'traseras' :
                                type === 'accesorio' ? 'accesorios' : null;
            if (materialsKey && materials[materialsKey] && materials[materialsKey][i]) {
              const materialItem = materials[materialsKey][i];
              productId = typeof materialItem === 'object' ? materialItem.productId : materialItem;
            }
          }

          console.log(`  → Creating new ${type} line: lineType=${lineType}, productId=${productId}, width=${item.width || calculatedWidth}, height=${item.height || calculatedHeight}`);
          if (productId) {
            // Use the price from the calculation (already includes client margin)
            // item.totalPrice is the price for 1 unit (from calculator)
            const displayedPriceSingle = item.totalPrice || 0;
            const adjustedDisplayedPriceSingle = isManualPrice ? displayedPriceSingle * priceAdjustmentFactor : displayedPriceSingle;
            const adjustedBasePriceSingle = isProOrder ? adjustedDisplayedPriceSingle : (adjustedDisplayedPriceSingle / vatMultiplier);
            const createUnits = editResult.formData.units || 1;

            try {
              const response = await api.post(`/orders/${order.id}/lines`, {
                lineType,
                productId,
                width: item.width || calculatedWidth,
                height: item.height || calculatedHeight,
                quantity: createUnits,
                notes: editResult.formData.notes || '',
                articleFingerprint,
                unitPrice: adjustedBasePriceSingle,
                totalPrice: adjustedBasePriceSingle * createUnits
              });
              console.log(`  → POST response:`, response.data);
            } catch (postError) {
              console.error(`  → POST FAILED for ${lineType}:`, postError.response?.data || postError.message);
            }
          } else {
            console.warn(`  → SKIPPED creating ${type} line - no productId found!`);
          }
        }
      }

      // Update order fields if changed (client, references)
      const orderPatch = {};
      const newClientId = editResult.formData.clientId;
      if (newClientId && newClientId !== order.clientId) {
        orderPatch.clientId = newClientId;
      }
      // Always update supplier/client references from the form
      if (editResult.formData.supplierReference !== undefined) {
        orderPatch.supplierReference = editResult.formData.supplierReference;
      }
      if (editResult.formData.clientReference !== undefined) {
        orderPatch.clientReference = editResult.formData.clientReference;
      }
      if (Object.keys(orderPatch).length > 0) {
        try {
          await api.patch(`/orders/${order.id}`, orderPatch);
        } catch (patchError) {
          console.error('Failed to update order fields:', patchError);
          toast.error('Artículo guardado pero no se pudieron actualizar las referencias');
        }
      }

      toast.success(isManualPrice ? 'Artículo actualizado con precio manual' : 'Artículo actualizado correctamente');
      setCalculatorModal(null);
      await refreshOrderCache(order.id);
      invalidateOrderQueries(order.id);
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error('Error al actualizar el artículo');
    }
  };

  const handleOpenNotes = (fingerprint, components, order) => {
    const firstComponent = components[0];
    setNotesModal({
      fingerprint,
      components,
      notes: firstComponent.notes || '',
      orderId: order.id
    });
    setOpenMenu(null);
  };

  const handleSaveNotes = async (notes) => {
    try {
      const { orderId, components } = notesModal;

      // Update notes for all components with the same fingerprint
      const updatePromises = components.map(component =>
        api.put(`/orders/${orderId}/lines/${component.id}`, {
          notes: notes
        })
      );

      await Promise.all(updatePromises);

      toast.success('Notas guardadas correctamente');
      setNotesModal(null);
      await refreshOrderCache(orderId);
      invalidateOrderQueries(orderId);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Error al guardar las notas');
    }
  };

  const handleArchiveOrder = async (order) => {
    if (!order) return;
    if (!window.confirm(`¿Archivar el pedido #${order.orderNumber}?`)) return;

    try {
      await orderService.archive(order.id);
      toast.success('Pedido archivado');
      invalidateOrderQueries(order.id);
    } catch (error) {
      console.error('Error archiving order:', error);
      toast.error('No se pudo archivar el pedido');
    }
  };

  const handleUnarchiveOrder = async (order) => {
    if (!order) return;
    if (!window.confirm(`¿Restaurar el pedido #${order.orderNumber} desde archivados?`)) return;

    try {
      await orderService.unarchive(order.id);
      toast.success('Pedido restaurado');
      setSelectedOrders(prev => prev.filter(selected => selected.id !== order.id));
      invalidateOrderQueries(order.id);
    } catch (error) {
      console.error('Error unarchiving order:', error);
      toast.error(error.response?.data?.error || 'No se pudo restaurar el pedido');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!order) {
      return;
    }

    if (!window.confirm(`¿Mover el pedido #${order.orderNumber} a eliminados?`)) {
      return;
    }

    try {
      await orderService.delete(order.id);

      toast.success('Pedido movido a eliminados');

      queryClient.setQueryData(['orders', filterStatus, currentPage, debouncedSearch], (existing) => {
        if (!existing || !Array.isArray(existing.orders)) {
          return existing;
        }
        const filteredOrders = existing.orders.filter(current => String(current.id) !== String(order.id));
        return {
          ...existing,
          orders: filteredOrders,
          total: typeof existing.total === 'number' ? Math.max(0, existing.total - 1) : existing.total,
        };
      });

      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders-counts'] });
      queryClient.invalidateQueries({ queryKey: ['orders-deleted'] });
      queryClient.removeQueries({ queryKey: ['order', String(order.id)] });
      setSelectedOrders(prev => prev.filter(selected => selected.id !== order.id));
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('No se pudo eliminar el pedido');
    }
  };

  const handleRestoreOrder = async (order) => {
    if (!order) {
      return;
    }

    try {
      await orderService.restore(order.id);

      toast.success('Pedido restaurado correctamente');

      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders-counts'] });
      queryClient.invalidateQueries({ queryKey: ['orders-deleted'] });
    } catch (error) {
      console.error('Error restoring order:', error);
      toast.error('No se pudo restaurar el pedido');
    }
  };

  const handleDeletePermanent = async (order) => {
    if (!order) {
      return;
    }

    if (!window.confirm(`¿Eliminar PERMANENTEMENTE el pedido #${order.orderNumber}? Esta acción NO se puede deshacer.`)) {
      return;
    }

    try {
      await orderService.deletePermanent(order.id);

      toast.success('Pedido eliminado permanentemente');

      queryClient.setQueryData(['orders', 'deleted', currentPage, debouncedSearch], (existing) => {
        if (!existing || !Array.isArray(existing.orders)) {
          return existing;
        }
        const filteredOrders = existing.orders.filter(current => String(current.id) !== String(order.id));
        return {
          ...existing,
          orders: filteredOrders,
          total: typeof existing.total === 'number' ? Math.max(0, existing.total - 1) : existing.total,
        };
      });

      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['orders-counts'] });
      queryClient.removeQueries({ queryKey: ['order', String(order.id)] });
    } catch (error) {
      console.error('Error permanently deleting order:', error);
      toast.error('No se pudo eliminar el pedido permanentemente');
    }
  };

  const handleViewOrder = (orderId) => {
    if (!orderId) {
      return;
    }
    setOpenMenu(null);
    navigate(`/orders/${orderId}`);
  };

  const handleDeleteLine = async (fingerprint, components, order) => {
    if (!components?.length) {
      return;
    }

    if (order && ['delivered', 'invoiced'].includes(order.status)) {
      toast.warning('No se pueden eliminar líneas de un pedido entregado o facturado');
      setOpenMenu(null);
      return;
    }

    const confirmMessage = components.length > 1
      ? `¿Eliminar la línea completa con ${components.length} componentes?`
      : '¿Eliminar este componente del pedido?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const orderId = order?.id || components[0]?.orderId;

    try {
      await Promise.all(
        components.map((component) =>
          api.delete(`/orders/${orderId}/lines/${component.id}`)
        )
      );

      toast.success('Línea eliminada correctamente');
      await refreshOrderCache(orderId);
      invalidateOrderQueries(orderId);
    } catch (error) {
      console.error('Error deleting line:', error);
      toast.error('Error al eliminar la línea');
    } finally {
      setOpenMenu(null);
    }
  };

  // Server already filters by view/search/pagination
  // Only apply local sourceFilter and lineStatusFilter
  const filteredOrders = (orders || []).filter(order => {
    if (sourceFilter) {
      const isPro = isCalculadoraPro(order);
      if (sourceFilter === 'pro' && !isPro) return false;
      if (sourceFilter === 'tpv' && isPro) return false;
    }
    if (lineStatusFilter) {
      const hasLineWithStatus = order.lines?.some(line => line.status === lineStatusFilter);
      if (!hasLineWithStatus) return false;
    }
    return true;
  }).sort((a, b) => {
    if (a.isImportant && !b.isImportant) return -1;
    if (!a.isImportant && b.isImportant) return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const totalPages = Math.max(1, Math.ceil(totalOrders / ITEMS_PER_PAGE));
  const paginatedOrders = filteredOrders;

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    const clampedPage = Math.min(Math.max(page, 1), totalPages);
    if (clampedPage !== currentPage) {
      setCurrentPage(clampedPage);
    }
  };

  // Toggle line expansion
  const toggleLineExpansion = (fingerprint, e) => {
    e.stopPropagation();
    setExpandedLines(prev => {
      const next = new Set(prev);
      if (next.has(fingerprint)) {
        next.delete(fingerprint);
      } else {
        next.add(fingerprint);
      }
      return next;
    });
  };

  // Toggle status dropdown
  const toggleStatusDropdown = (fingerprint, e) => {
    e.stopPropagation();
    if (openStatusDropdown === fingerprint) {
      setOpenStatusDropdown(null);
    } else {
      // Calculate position based on button
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
      setOpenStatusDropdown(fingerprint);
    }
  };

  // Handle status change from dropdown
  const handleStatusFromDropdown = async (newStatus, components, fingerprint, e) => {
    e.stopPropagation();
    setOpenStatusDropdown(null);
    await handleQuickStatusChange(components[0].id, newStatus, components, fingerprint);
  };

  if (isLoading) {
    return (
      <OrdersContainer>
        <LoadingState>Cargando pedidos...</LoadingState>
      </OrdersContainer>
    );
  }

  if (error) {
    return (
      <OrdersContainer>
        <EmptyState>Error al cargar los pedidos.</EmptyState>
      </OrdersContainer>
    );
  }

  return (
    <OrdersContainer>
      <Header>
        <Title>Pedidos</Title>
        <HeaderControls>
          <SearchBar
            type="text"
            placeholder="Buscar por número, cliente o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Selection controls for pending tab (work orders) */}
          {filterStatus === 'pending' && (
            <>
              <SelectAllButton onClick={selectedOrders.length > 0 ? handleDeselectAll : handleSelectAllPending}>
                {selectedOrders.length > 0 ? (
                  <>
                    <FaTimes />
                    Deseleccionar ({selectedOrders.length})
                  </>
                ) : (
                  <>
                    <FaCheckSquare />
                    Seleccionar
                  </>
                )}
              </SelectAllButton>

              <DeliveryNoteButton
                onClick={handleCreateCombinedWorkOrder}
                disabled={selectedOrders.length === 0}
                style={{ background: '#6f42c1' }}
              >
                <FaClipboardList />
                Orden de Taller
                {selectedOrders.length > 0 && (
                  <SelectionCount>{selectedOrders.length}</SelectionCount>
                )}
              </DeliveryNoteButton>
            </>
          )}

          {/* Selection controls for archived tab (work orders only) */}
          {filterStatus === 'archived' && (
            <>
              <SelectAllButton onClick={selectedOrders.length > 0 ? handleDeselectAll : () => {
                if (filteredOrders.length === 0) {
                  toast.info('No hay pedidos archivados');
                  return;
                }
                setSelectedOrders(filteredOrders);
                toast.success(`${filteredOrders.length} pedido(s) seleccionado(s)`);
              }}>
                {selectedOrders.length > 0 ? (
                  <>
                    <FaTimes />
                    Deseleccionar ({selectedOrders.length})
                  </>
                ) : (
                  <>
                    <FaCheckSquare />
                    Seleccionar
                  </>
                )}
              </SelectAllButton>

              <DeliveryNoteButton
                onClick={handleCreateCombinedWorkOrder}
                disabled={selectedOrders.length === 0}
                style={{ background: '#6f42c1' }}
              >
                <FaClipboardList />
                Orden de Taller
                {selectedOrders.length > 0 && (
                  <SelectionCount>{selectedOrders.length}</SelectionCount>
                )}
              </DeliveryNoteButton>
            </>
          )}

          {/* Selection controls for completed tab (delivery notes) */}
          {filterStatus === 'completed' && (
            <>
              <SelectAllButton onClick={selectedOrders.length > 0 ? handleDeselectAll : handleSelectAll}>
                {selectedOrders.length > 0 ? (
                  <>
                    <FaTimes />
                    Deseleccionar ({selectedOrders.length})
                  </>
                ) : (
                  <>
                    <FaCheckSquare />
                    Seleccionar Todos
                  </>
                )}
              </SelectAllButton>

              <DeliveryNoteButton
                onClick={handleCreateCombinedWorkOrder}
                disabled={selectedOrders.length === 0}
                style={{ background: '#6f42c1' }}
              >
                <FaClipboardList />
                Orden de Taller
                {selectedOrders.length > 0 && (
                  <SelectionCount>{selectedOrders.length}</SelectionCount>
                )}
              </DeliveryNoteButton>

              <DeliveryNoteButton
                onClick={handleCreateDeliveryNotes}
                disabled={selectedOrders.length === 0}
              >
                <FaTruck />
                Crear Albarán
                {selectedOrders.length > 0 && (
                  <SelectionCount>{selectedOrders.length}</SelectionCount>
                )}
              </DeliveryNoteButton>
            </>
          )}

          <SyncButton onClick={handleSyncOdoo} disabled={isSyncing}>
            <FaSync className={isSyncing ? 'spinning' : ''} />
            {isSyncing ? 'Sincronizando...' : 'Sincronizar a Odoo'}
          </SyncButton>

          <ImportButton onClick={handleImportOrdersFromOdoo} disabled={isImportingOdoo}>
            <FaCloudDownloadAlt className={isImportingOdoo ? 'spinning' : ''} />
            {isImportingOdoo ? 'Importando...' : 'Importar de Odoo'}
          </ImportButton>
        </HeaderControls>
      </Header>

      <StatusFiltersContainer>
        {/* Source filter: PRO / TPV */}
        <StatusFilterButton
          $active={sourceFilter === 'pro'}
          $color="#2196F3"
          onClick={() => setSourceFilter(sourceFilter === 'pro' ? null : 'pro')}
          style={{ fontWeight: 700 }}
        >
          PRO
        </StatusFilterButton>
        <StatusFilterButton
          $active={sourceFilter === 'tpv'}
          $color="#ff9800"
          onClick={() => setSourceFilter(sourceFilter === 'tpv' ? null : 'tpv')}
          style={{ fontWeight: 700 }}
        >
          TPV
        </StatusFilterButton>

        <span style={{ margin: '0 0.5rem', color: '#ccc' }}>|</span>

        {/* Line status filters */}
        {STATUS_CONFIG.filter(s => s.key !== 'invoiced').map(status => (
          <StatusFilterButton
            key={status.key}
            $active={lineStatusFilter === status.key}
            $color={status.color}
            onClick={() => setLineStatusFilter(lineStatusFilter === status.key ? null : status.key)}
          >
            {status.label}
          </StatusFilterButton>
        ))}
        {(lineStatusFilter || sourceFilter) && (
          <ClearFilterButton onClick={() => { setLineStatusFilter(null); setSourceFilter(null); }}>
            <FaTimes />
          </ClearFilterButton>
        )}
      </StatusFiltersContainer>

      <FilterTabs>
        <FilterTab
          $active={filterStatus === 'pending'}
          onClick={() => setFilterStatus('pending')}
        >
          Pendientes de Realizar ({tabCounts?.pending || 0})
        </FilterTab>
        <FilterTab
          $active={filterStatus === 'completed'}
          onClick={() => setFilterStatus('completed')}
        >
          Ya Realizados ({tabCounts?.completed || 0})
        </FilterTab>
        <FilterTab
          $active={filterStatus === 'archived'}
          onClick={() => setFilterStatus('archived')}
        >
          Archivados ({tabCounts?.archived || 0})
        </FilterTab>
        <FilterTab
          $active={filterStatus === 'all'}
          onClick={() => setFilterStatus('all')}
        >
          Todos ({tabCounts?.all || 0})
        </FilterTab>
        <FilterTab
          $active={filterStatus === 'deleted'}
          onClick={() => setFilterStatus('deleted')}
          style={{ color: filterStatus === 'deleted' ? 'white' : '#dc3545', borderColor: filterStatus === 'deleted' ? '#dc3545' : 'transparent', background: filterStatus === 'deleted' ? '#dc3545' : 'transparent' }}
        >
          Eliminados ({deletedOrdersCount})
        </FilterTab>
      </FilterTabs>

      {filteredOrders.length === 0 ? (
        <EmptyState>
          {searchTerm
            ? 'No se encontraron pedidos'
            : filterStatus === 'pending'
              ? 'No hay pedidos pendientes de realizar'
              : filterStatus === 'completed'
                ? 'No hay pedidos ya realizados'
                : filterStatus === 'archived'
                  ? 'No hay pedidos archivados'
                  : filterStatus === 'deleted'
                    ? 'No hay pedidos eliminados'
                    : 'No hay pedidos'}
        </EmptyState>
      ) : (
        paginatedOrders.map(order => {
          const isProfessional = isCalculadoraPro(order);
          const showPriceWithVAT = !isProfessional;

          // Group lines by article identity.
          // IMPORTANT: fingerprint alone can collide when there are multiple articles
          // with similar materials; include calculationIndex when available.
          const groupedLines = {};
          (order.lines || []).forEach(line => {
            let metadata = line.metadata || {};
            if (typeof metadata === 'string') {
              try {
                metadata = JSON.parse(metadata);
              } catch (error) {
                metadata = {};
              }
            }

            const groupKey = metadata.fingerprint || `line-${line.id}`;

            if (!groupedLines[groupKey]) {
              groupedLines[groupKey] = [];
            }
            groupedLines[groupKey].push(line);
          });

          const lineGroups = Object.entries(groupedLines);

          // TPV vs PRO detection
          const isTPV = !isCalculadoraPro(order);

          // Pre-compute TPV display prices: each line with IVA + 5-cent rounding
          const groupDisplayTotals = {};
          let tpvDisplayTotal = 0;
          if (isTPV) {
            const hasManualPrice = order.metadata?.isManualPrice && order.metadata?.manualFinalAmount;
            let rawSum = 0;
            lineGroups.forEach(([fp, components]) => {
              const groupBaseTotal = components.reduce((sum, comp) => sum + parseFloat(comp.totalPrice || 0), 0);
              const groupQuantity = Math.max(...components.map(comp => parseFloat(comp.quantity || 1))) || 1;
              const unitBase = groupBaseTotal / groupQuantity;
              const displayPrice = roundToFiveCents(unitBase * 1.21) * groupQuantity;
              groupDisplayTotals[fp] = displayPrice;
              rawSum += displayPrice;
            });
            // Use manual price or stored finalAmount as authoritative total
            if (hasManualPrice) {
              tpvDisplayTotal = parseFloat(order.metadata.manualFinalAmount);
            } else if (order.finalAmount) {
              tpvDisplayTotal = roundToFiveCents(parseFloat(order.finalAmount));
            } else {
              tpvDisplayTotal = rawSum;
            }
            // Adjust line prices proportionally so they match the real total
            if (rawSum > 0 && Math.abs(rawSum - tpvDisplayTotal) > 0.001) {
              const ratio = tpvDisplayTotal / rawSum;
              lineGroups.forEach(([fp]) => {
                groupDisplayTotals[fp] = Math.round(groupDisplayTotals[fp] * ratio * 100) / 100;
              });
            }
          }

          const isSelected = selectedOrders.some(o => o.id === order.id);
          const canModifyOrder = !['delivered', 'invoiced'].includes(order.status);

          // Check if ALL lines are ready or finished (order is complete)
          const allLinesReady = order.lines?.length > 0 && order.lines.every(line =>
            ['ready', 'finished'].includes(line.status)
          );

          // Can be delivered only if ALL lines are ready/finished and order is not archived
          const canBeDelivered = !order.isArchived && allLinesReady;

          // Debug
          console.log(`Order #${order.orderNumber}:`, {
            lineStatuses: order.lines?.map(l => l.status),
            allLinesReady,
            isArchived: order.isArchived,
            canBeDelivered,
            isSelected
          });

          return (
            <OrderCard key={order.id} $isProfessional={isProfessional} $isSelected={isSelected} $isUrgent={order.isImportant}>
              <OrderHeader $isProfessional={isProfessional} $isSelected={isSelected} $isUrgent={order.isImportant}>
                <OrderCheckbox
                  className={isSelected ? 'selected' : ''}
                  onClick={() => {
                    // In pending tab, allow selection for work orders
                    // In completed tab, only allow if all lines are ready
                    if (filterStatus === 'archived') {
                      handleToggleOrderSelection(order);
                    } else if (filterStatus === 'pending') {
                      if (!order.isArchived) {
                        handleToggleOrderSelection(order);
                      } else {
                        toast.warning('No se puede seleccionar un pedido archivado');
                      }
                    } else if (canBeDelivered) {
                      handleToggleOrderSelection(order);
                    } else {
                      toast.warning('Este pedido no está completo. Todas las líneas deben estar en estado "Listo" o "Enmarcado"');
                    }
                  }}
                  style={{
                    opacity: filterStatus === 'archived' || (filterStatus === 'pending' && !order.isArchived) || canBeDelivered ? 1 : 0.3,
                    cursor: filterStatus === 'archived' || (filterStatus === 'pending' && !order.isArchived) || canBeDelivered ? 'pointer' : 'not-allowed'
                  }}
                >
                  {isSelected ? <FaCheckSquare /> : <FaSquare />}
                </OrderCheckbox>

                <OrderNumber>#{order.orderNumber}</OrderNumber>

                <TypeBadge $isProfessional={isProfessional}>
                  {isProfessional ? 'PRO' : 'TPV'}
                </TypeBadge>

                {order.isImportant && (
                  <button
                    onClick={(e) => handleToggleImportant(order, e)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.1rem 0.4rem',
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                    title="Desmarcar como urgente"
                  >
                    URGENTE ✕
                  </button>
                )}

                {filterStatus === 'pending' && !order.isImportant && (
                  <button
                    onClick={(e) => handleToggleImportant(order, e)}
                    style={{
                      background: 'transparent',
                      border: '1px solid #ddd',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      padding: '0.1rem 0.4rem',
                      fontSize: '0.6rem',
                      color: '#999',
                      fontWeight: '500'
                    }}
                    title="Marcar como urgente"
                  >
                    + Urgente
                  </button>
                )}

                <OrderValue style={{ flex: 1, minWidth: 0 }}>
                  {order.client?.name || 'Sin cliente'}
                  {order.client?.name?.toUpperCase().includes('LEROY') && (order.clientReference || order.supplierReference) && (
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '1px' }}>
                      {[order.clientReference, order.supplierReference].filter(Boolean).join(' - ')}
                    </div>
                  )}
                </OrderValue>

                <OrderActions>
                  <ViewButton
                    onClick={() => setImagesModal({ order, lines: order.lines || [] })}
                    title="Ver materiales"
                    style={{ background: '#6f42c1' }}
                  >
                    <FaImages />
                  </ViewButton>
                  <ViewButton
                    onClick={() => generateWorkshopPDF(order)}
                    title="PDF Taller"
                    style={{ background: '#17a2b8' }}
                  >
                    <FaPrint />
                  </ViewButton>
                  <ViewButton onClick={() => handleViewOrder(order.id)} title="Ver pedido">
                    <FaEye />
                  </ViewButton>
                  {/* Show sync button for TPV orders not yet synced to Odoo */}
                  {!isProfessional && !order.odooId && !order.metadata?.odooSync?.success && filterStatus !== 'deleted' && (
                    <ViewButton
                      onClick={(e) => handleSyncSingleOrderToOdoo(order.id, order.orderNumber, e)}
                      title="Sincronizar a Odoo"
                      disabled={syncingOrderId === order.id}
                      style={{
                        background: syncingOrderId === order.id ? '#6c757d' : '#28a745',
                        opacity: syncingOrderId === order.id ? 0.7 : 1,
                        pointerEvents: syncingOrderId ? 'none' : 'auto'
                      }}
                    >
                      {syncingOrderId === order.id ? <FaSync className="spinning" /> : <FaUpload />}
                    </ViewButton>
                  )}
                  {filterStatus === 'deleted' ? (
                    <>
                      <RestoreButton onClick={() => handleRestoreOrder(order)} title="Restaurar pedido">
                        <FaUndo />
                      </RestoreButton>
                      <DeleteButton onClick={() => handleDeletePermanent(order)}>
                        <FaTrashAlt />
                      </DeleteButton>
                    </>
                  ) : (
                    <>
                      {order.status === 'delivered' && (
                        <RestoreButton onClick={() => handleUnarchiveOrder(order)} title="Restaurar desde archivados">
                          <FaUndo />
                        </RestoreButton>
                      )}
                      {canModifyOrder && (
                        <ViewButton
                          onClick={() => handleArchiveOrder(order)}
                          title="Archivar pedido"
                          style={{ background: '#6c757d' }}
                        >
                          <FaArchive />
                        </ViewButton>
                      )}
                      {canModifyOrder && (
                        <DeleteButton onClick={() => handleDeleteOrder(order)}>
                          <FaTrash />
                        </DeleteButton>
                      )}
                    </>
                  )}
                </OrderActions>
              </OrderHeader>

              {order.notes && (
                <OrderNotes $isProfessional={isProfessional}>
                  📝 {order.notes}
                </OrderNotes>
              )}

              {order.metadata?.targetDeliveryDate && (
                <div style={{
                  padding: '0.2rem 0.75rem 0.3rem',
                  fontSize: '0.75rem',
                  color: '#e67e22',
                  fontWeight: 600
                }}>
                  📅 Entrega: {new Date(order.metadata.targetDeliveryDate).toLocaleDateString('es-ES')}
                </div>
              )}

              <OrderLinesContainer>
                {lineGroups.map(([fingerprint, components], groupIndex) => {
                  // Calculate total for this line (all components) using BASE PRICES WITHOUT VAT
                  const groupTotal = components.reduce((sum, comp) => {
                    return sum + parseFloat(comp.totalPrice || 0);
                  }, 0);

                  // Get dimensions from first component
                  const firstComponent = components[0];
                  const fcMeta = typeof firstComponent.metadata === 'string'
                    ? JSON.parse(firstComponent.metadata || '{}')
                    : (firstComponent.metadata || {});
                  let dimW = firstComponent.width;
                  let dimH = firstComponent.height;
                  // For INT measurement, show expanded (glass/passepartout) dimensions
                  if (fcMeta.originalMeasurementType === 'INT') {
                    const glassPasse = components.filter(c =>
                      c.lineType?.includes('cristal') || c.lineType?.includes('passepartout')
                    );
                    let maxW = 0, maxH = 0;
                    glassPasse.forEach(c => {
                      const cw = parseFloat(c.width || 0);
                      const ch = parseFloat(c.height || 0);
                      if (cw * ch > maxW * maxH) { maxW = cw; maxH = ch; }
                    });
                    if (maxW > 0 && maxH > 0) { dimW = maxW; dimH = maxH; }
                  }
                  const dimensions = parseFloat(dimW) > 0 && parseFloat(dimH) > 0
                    ? `${dimW} × ${dimH} cm`
                    : '';

                  // Determine the most critical status among all components
                  // Priority: out_of_stock > pending > confirmed > reviewed > cut > stapled > finished > ready > delivered
                  const statusPriority = {
                    'out_of_stock': 1,
                    'pending': 2,
                    'confirmed': 3,
                    'reviewed': 4,
                    'cut': 5,
                    'stapled': 6,
                    'finished': 7,
                    'ready': 8,
                    'delivered': 9
                  };

                  const currentStatus = components.reduce((mostCritical, comp) => {
                    const compStatus = comp.status || 'pending';
                    // Use the first status if mostCritical is null/undefined
                    if (!mostCritical) return compStatus;

                    const currentPriority = statusPriority[mostCritical] || 999;
                    const newPriority = statusPriority[compStatus] || 999;
                    return newPriority < currentPriority ? compStatus : mostCritical;
                  }, null);

                  // Build full product description
                  const buildProductDescription = (components) => {
                    const firstLine = components[0];

                    // Try to get ORIGINAL dimensions from metadata (what user entered)
                    const metadata = typeof firstLine.metadata === 'string'
                      ? JSON.parse(firstLine.metadata || '{}')
                      : (firstLine.metadata || {});

                    // Check for custom description - overrides auto-generated
                    if (metadata.customDescription) {
                      return metadata.customDescription;
                    }

                    const hasOriginalDimensions = metadata.originalWidth && metadata.originalHeight;

                    // Use original dimensions if available, otherwise fall back to line dimensions
                    let displayWidth, displayHeight, displayMeasurementType;

                    if (hasOriginalDimensions) {
                      displayMeasurementType = metadata.originalMeasurementType || 'INT';

                      // For INTERIOR orders, show the glass/passepartout dimensions (the largest)
                      // instead of the original interior dimensions
                      if (displayMeasurementType === 'INT') {
                        const glassPasse = components.filter(l =>
                          l.lineType?.includes('cristal') || l.lineType?.includes('passepartout')
                        );
                        let maxW = 0, maxH = 0;
                        glassPasse.forEach(l => {
                          const lw = parseFloat(l.width || 0);
                          const lh = parseFloat(l.height || 0);
                          if (lw * lh > maxW * maxH) { maxW = lw; maxH = lh; }
                        });
                        if (maxW > 0 && maxH > 0) {
                          displayWidth = maxW;
                          displayHeight = maxH;
                        } else {
                          displayWidth = metadata.originalWidth;
                          displayHeight = metadata.originalHeight;
                        }
                      } else {
                        displayWidth = metadata.originalWidth;
                        displayHeight = metadata.originalHeight;
                      }
                    } else {
                      displayWidth = firstLine.width;
                      displayHeight = firstLine.height;
                      displayMeasurementType = firstLine.measurementType || '';
                    }

                    const dimensions = parseFloat(displayWidth) > 0 && parseFloat(displayHeight) > 0
                      ? `${displayWidth}x${displayHeight}`
                      : '';
                    const measurementType = parseFloat(displayWidth) > 0 && parseFloat(displayHeight) > 0
                      ? displayMeasurementType
                      : '';

                    // Start with measurement type and dimensions
                    let description = '';
                    if (measurementType) {
                      description += measurementType;
                    }
                    if (dimensions) {
                      description += description ? ` - ${dimensions}` : dimensions;
                    }

                    // Build components list
                    const componentDescriptions = [];

                    const molduras = components.filter(l => l.lineType === 'moldura');
                    const passepartouts = components.filter(l => l.lineType?.includes('passepartout'));
                    const cristales = components.filter(l => l.lineType?.includes('cristal'));
                    const traseras = components.filter(l => l.lineType?.includes('trasera'));
                    const accesorios = components.filter(l => l.lineType?.includes('accesorio'));

                    if (molduras.length > 0) {
                      const molduraNames = molduras.map(m => m.product?.name).filter(Boolean);
                      if (molduraNames.length > 0) {
                        componentDescriptions.push(`Moldura ${molduraNames.join(' + ')}`);
                      }
                    }

                    if (passepartouts.length > 0) {
                      const passepartoutNames = passepartouts.map(p => {
                        const name = p.product?.name;
                        const width = p.metadata?.passepartoutWidth;
                        return name && width ? `${name} (${width}cm)` : name;
                      }).filter(Boolean);
                      if (passepartoutNames.length > 0) {
                        componentDescriptions.push(`Passepartout ${passepartoutNames.join(' + ')}`);
                      }
                    }

                    if (cristales.length > 0) {
                      const cristalNames = cristales.map(c => c.product?.name).filter(Boolean);
                      if (cristalNames.length > 0) {
                        componentDescriptions.push(`Cristal ${cristalNames.join(' + ')}`);
                      }
                    }

                    if (traseras.length > 0) {
                      const traseraNames = traseras.map(t => t.product?.name).filter(Boolean);
                      if (traseraNames.length > 0) {
                        componentDescriptions.push(`Trasera ${traseraNames.join(' + ')}`);
                      }
                    }

                    if (accesorios.length > 0) {
                      const accesorioNames = accesorios.map(a => a.product?.name).filter(Boolean);
                      if (accesorioNames.length > 0) {
                        componentDescriptions.push(`Accesorios ${accesorioNames.join(', ')}`);
                      }
                    }

                    // Join components with " - "
                    if (componentDescriptions.length > 0) {
                      description += description ? ` - ${componentDescriptions.join(' - ')}` : componentDescriptions.join(' - ');
                    }

                    return description || 'Marco';
                  };

                  const fullDescription = buildProductDescription(components);
                  const isExpanded = expandedLines.has(fingerprint);
                  const currentStatusConfig = STATUS_CONFIG.find(s => s.key === currentStatus) || STATUS_CONFIG[0];

                  return (
                    <OrderLine key={fingerprint} $expanded={isExpanded}>
                      {/* Compact row - always visible */}
                      <LineCompactRow>
                        <ExpandButton
                          $expanded={isExpanded}
                          onClick={(e) => toggleLineExpansion(fingerprint, e)}
                          title={isExpanded ? 'Colapsar' : 'Expandir'}
                        >
                          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        </ExpandButton>

                        <LineNumber>{groupIndex + 1}</LineNumber>

                        <LineDescription>
                          <ProductName $compact={!isExpanded}>{fullDescription}</ProductName>
                          {firstComponent.notes && (
                            <span style={{ fontSize: '0.95rem', color: '#666', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                              📝 {firstComponent.notes}
                            </span>
                          )}
                        </LineDescription>

                        <LineQuantity>x{firstComponent.quantity || 1}</LineQuantity>

                        <LinePriceColumn $compact>
                          <span className="total-price">{formatCurrency(isTPV ? groupDisplayTotals[fingerprint] : groupTotal)}</span>
                        </LinePriceColumn>

                        {/* Status dropdown for compact view */}
                        {order.status === 'invoiced' ? (
                          <StatusBadge $color="#28a745" disabled style={{ cursor: 'not-allowed' }}>
                            Facturado
                          </StatusBadge>
                        ) : (
                          <StatusDropdown data-status-dropdown={fingerprint}>
                            <StatusBadge
                              $color={currentStatusConfig.color}
                              onClick={(e) => toggleStatusDropdown(fingerprint, e)}
                              disabled={updatingFingerprints.has(fingerprint)}
                            >
                              {currentStatusConfig.label}
                              <FaChevronDown />
                            </StatusBadge>
                            {openStatusDropdown === fingerprint && (
                              <StatusDropdownMenu
                                data-status-dropdown={fingerprint}
                                style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                              >
                                {STATUS_CONFIG.filter(s => s.key !== 'invoiced').map(status => (
                                  <StatusDropdownItem
                                    key={status.key}
                                    $color={status.color}
                                    $active={currentStatus === status.key}
                                    onClick={(e) => handleStatusFromDropdown(status.key, components, fingerprint, e)}
                                  >
                                    <span className="status-dot" />
                                    {status.label}
                                  </StatusDropdownItem>
                                ))}
                              </StatusDropdownMenu>
                            )}
                          </StatusDropdown>
                        )}

                        {canModifyOrder && (
                          <MenuWrapper data-menu-wrapper={fingerprint}>
                            <MenuButton
                              data-menu-wrapper={fingerprint}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openMenu === fingerprint) {
                                  setOpenMenu(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPosition({
                                    top: rect.bottom + 4,
                                    left: rect.right - 160 // Align to right edge of button
                                  });
                                  setOpenMenu(fingerprint);
                                }
                              }}
                            >
                              <FaEllipsisV />
                            </MenuButton>
                            {openMenu === fingerprint && (
                              <DropdownMenu
                                data-menu-wrapper={fingerprint}
                                style={{ top: menuPosition.top, left: menuPosition.left }}
                              >
                                <MenuItem data-menu-wrapper={fingerprint} onClick={() => handleEditLine(fingerprint, components, order)}>
                                  <FaEdit /> Editar
                                </MenuItem>
                                <MenuItem data-menu-wrapper={fingerprint} onClick={() => handleOpenNotes(fingerprint, components, order)}>
                                  <FaStickyNote /> {firstComponent.notes ? 'Notas' : 'Añadir notas'}
                                </MenuItem>
                                <MenuItem data-menu-wrapper={fingerprint} onClick={() => handleDeleteLine(fingerprint, components, order)}>
                                  <FaTrash /> Eliminar
                                </MenuItem>
                              </DropdownMenu>
                            )}
                          </MenuWrapper>
                        )}
                      </LineCompactRow>

                      {/* Expanded section - shown when expanded */}
                      {isExpanded && (
                        <LineExpandedSection>
                          {/* Single line: Status buttons + Price + Notes indicator */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            {/* Status buttons */}
                            {order.status !== 'invoiced' && (
                              <StatusButtonsContainer>
                                {STATUS_CONFIG.filter(s => s.key !== 'invoiced').map(status => (
                                  <StatusButton
                                    key={status.key}
                                    $active={currentStatus === status.key}
                                    $color={status.color}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickStatusChange(firstComponent.id, status.key, components, fingerprint);
                                    }}
                                    disabled={updatingFingerprints.has(fingerprint)}
                                    title={status.label}
                                  >
                                    {status.initial}
                                  </StatusButton>
                                ))}
                              </StatusButtonsContainer>
                            )}

                            {/* Price details */}
                            <span style={{ fontSize: '0.75rem', color: '#666' }}>
                              {formatCurrency(groupTotal / (firstComponent.quantity || 1))} x {firstComponent.quantity || 1} = <strong>{formatCurrency(groupTotal)}</strong>
                            </span>

                            {/* Notes */}
                            {firstComponent.notes && (
                              <span style={{ fontSize: '0.95rem', color: '#666', fontStyle: 'italic' }}>
                                📝 {firstComponent.notes}
                              </span>
                            )}
                          </div>
                        </LineExpandedSection>
                      )}
                    </OrderLine>
                  );
                })}
              </OrderLinesContainer>

              <OrderFooter $isProfessional={isProfessional}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.72rem', color: '#666' }}>
                  <span>{formatDate(order.createdAt)} {formatTime(order.createdAt)}</span>
                  <span>{lineGroups.length} {lineGroups.length === 1 ? 'línea' : 'líneas'}</span>
                </div>
                <TotalSection>
                  <TotalAmount>
                    {formatCurrency(
                      // TPV: finalAmount ya lleva el descuento aplicado (semántica POST, jul-2026) —
                      // no volver a restarlo aquí (doblaba el dto, bug #2132)
                      isTPV
                        ? tpvDisplayTotal
                        : calculateDisplayBaseAmount(order)
                    )}
                    <span style={{ fontSize: '0.65rem', fontWeight: 400, color: '#888', marginLeft: '0.25rem' }}>
                      {showPriceWithVAT ? 'IVA inc.' : 'sin IVA'}
                    </span>
                  </TotalAmount>
                  {parseFloat(order.discountPercentage) > 0 && (
                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#d9534f' }}>
                      Descuento {parseFloat(order.discountPercentage)}%
                    </span>
                  )}
                </TotalSection>
              </OrderFooter>
            </OrderCard>
          );
        })
      )}

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          >
            Primera
          </PaginationButton>
          <PaginationButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Anterior
          </PaginationButton>

          <PaginationInfo>
            Página {currentPage} de {totalPages} ({totalOrders} pedidos)
          </PaginationInfo>

          <PaginationButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Siguiente
          </PaginationButton>
          <PaginationButton
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          >
            Última
          </PaginationButton>
        </PaginationContainer>
      )}

      {editModal && (
        <EditLineModal
          lineData={editModal}
          onClose={() => setEditModal(null)}
          onSave={handleSaveLine}
          showPriceWithVAT={editModal.showPriceWithVAT}
        />
      )}

      {outOfStockModal && (
        <OutOfStockModal
          lineData={outOfStockModal}
          onClose={() => setOutOfStockModal(null)}
          onSave={handleSaveOutOfStock}
        />
      )}

      {notesModal && (
        <LineNotesModal
          lineData={notesModal}
          onClose={() => setNotesModal(null)}
          onSave={handleSaveNotes}
        />
      )}

      {statusModal && (
        <StatusModal onClick={() => setStatusModal(null)}>
          <StatusModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Cambiar Estado</h3>
            <StatusOption onClick={() => handleStatusChange('pending')}>
              <StatusDotLarge color={getStatusColor('pending')} />
              {formatOrderStatus('pending')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('confirmed')}>
              <StatusDotLarge color={getStatusColor('confirmed')} />
              {formatOrderStatus('confirmed')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('reviewed')}>
              <StatusDotLarge color={getStatusColor('reviewed')} />
              {formatOrderStatus('reviewed')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('cut')}>
              <StatusDotLarge color={getStatusColor('cut')} />
              {formatOrderStatus('cut')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('stapled')}>
              <StatusDotLarge color={getStatusColor('stapled')} />
              {formatOrderStatus('stapled')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('finished')}>
              <StatusDotLarge color={getStatusColor('finished')} />
              {formatOrderStatus('finished')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('ready')}>
              <StatusDotLarge color={getStatusColor('ready')} />
              {formatOrderStatus('ready')}
            </StatusOption>
            <StatusOption onClick={() => handleStatusChange('delivered')}>
              <StatusDotLarge color={getStatusColor('delivered')} />
              {formatOrderStatus('delivered')}
            </StatusOption>
          </StatusModalContent>
        </StatusModal>
      )}

      {showDeliveryNoteModal && (
        <CreateDeliveryNoteModal
          orders={selectedOrders}
          onClose={() => setShowDeliveryNoteModal(false)}
          onSuccess={() => {
            setSelectedOrders([]);
            setShowDeliveryNoteModal(false);
          }}
        />
      )}

      {calculatorModal && (
        <CalculatorEditModal
          article={calculatorModal.article}
          order={calculatorModal.order}
          onSave={handleCalculatorEditSave}
          onClose={() => setCalculatorModal(null)}
        />
      )}

      {imagesModal && (
        <ImagesModal
          orderId={imagesModal.order?.id}
          orderNumber={imagesModal.order?.orderNumber}
          onClose={() => setImagesModal(null)}
        />
      )}

      {showWorkOrderSelector && (
        <WorkOrderSelectorModal onClick={() => setShowWorkOrderSelector(false)}>
          <WorkOrderSelectorContent onClick={(e) => e.stopPropagation()}>
            <h3>Tipo de Orden de Taller</h3>
            <WorkOrderOption onClick={() => handleGenerateWorkOrder('simple')}>
              <div className="option-title">
                <FaClipboardList /> Sencilla
              </div>
              <div className="option-description">
                Solo descripción y notas, sin logo ni datos de empresa
              </div>
            </WorkOrderOption>
            <WorkOrderOption onClick={() => handleGenerateWorkOrder('detailed')}>
              <div className="option-title">
                <FaPrint /> Detallada
              </div>
              <div className="option-description">
                Con logo, datos de empresa y desglose completo de materiales
              </div>
            </WorkOrderOption>
            <WorkOrderModalActions>
              <CancelButton onClick={() => setShowWorkOrderSelector(false)}>
                Cancelar
              </CancelButton>
            </WorkOrderModalActions>
          </WorkOrderSelectorContent>
        </WorkOrderSelectorModal>
      )}
    </OrdersContainer>
  );
};

export default Orders;
